import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { logEvent } from "@/lib/analytics";
import { BucketBadge, Empty } from "@/components/ui-bits";
import { InfoTip } from "@/components/InfoTip";
import { fmtINR, fmtNum } from "@/lib/format";
import { QuickActionDialog } from "@/components/QuickActionDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  CurrencyInr, ClockCountdown, Handshake, Target, Pulse, Lightning, ArrowRight,
  DotsThreeVertical, PhoneCall, CalendarPlus,
} from "@phosphor-icons/react";

const scoreColor = (s) => (s >= 75 ? "#16A34A" : s >= 60 ? "#0B3D91" : s >= 40 ? "#D97706" : "#DC2626");
const catColor = (cat) => ({
  Excellent: "bg-emerald-100 text-emerald-800",
  Good: "bg-blue-100 text-blue-800",
  "Moderate Risk": "bg-amber-100 text-amber-800",
  "High Risk": "bg-orange-100 text-orange-800",
  Critical: "bg-red-100 text-red-800",
}[cat] || "bg-secondary text-muted-foreground");

const probColor = (p) => {
  const v = (p || "").toLowerCase();
  if (v === "high") return "bg-emerald-100 text-emerald-800";
  if (v === "medium") return "bg-amber-100 text-amber-800";
  if (v === "low") return "bg-red-100 text-red-800";
  return "bg-secondary text-muted-foreground";
};

const actionColor = (a) => {
  if (/legal/i.test(a)) return "bg-rose-100 text-rose-800";
  if (/immediately|missed/i.test(a)) return "bg-red-100 text-red-800";
  if (/settlement/i.test(a)) return "bg-violet-100 text-violet-800";
  if (/senior/i.test(a)) return "bg-orange-100 text-orange-800";
  if (/due today/i.test(a)) return "bg-amber-100 text-amber-800";
  return "bg-blue-100 text-blue-800";
};

function HealthGauge({ score, size = 144 }) {
  const color = scoreColor(score);
  const r = 52, c = 2 * Math.PI * r, off = c * (1 - score / 100);
  return (
    <div className="relative shrink-0" style={{ height: size, width: size }} data-testid="health-gauge">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="currentColor" strokeWidth="10" className="text-secondary" />
        <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-black font-heading" style={{ color }} data-testid="health-score-value">{score}</span>
        <span className="label-caps">/ 100</span>
      </div>
    </div>
  );
}

const BREAKDOWN = [
  { key: "outstanding_ratio", label: "Outstanding Ratio", weight: "30%" },
  { key: "followup_compliance", label: "Follow-up Compliance", weight: "25%" },
  { key: "recovery_rate", label: "Recovery Rate", weight: "20%" },
  { key: "settlement_success_rate", label: "Settlement Success", weight: "15%" },
  { key: "ageing", label: "Ageing", weight: "10%" },
];

export function InsightsPanel({ compact = false, hero = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qa, setQa] = useState({ open: false, action: "call", account: null });

  const load = () => api.get("/dashboard/money-stuck")
    .then((r) => {
      setData(r.data);
      try {
        const d = r.data || {};
        logEvent("MoneyStuckAnalyzed", {
          total_outstanding: d.total_outstanding || 0,
          accounts_needing_followup: d.followup_count || 0,
          view: compact ? "dashboard" : "full",
        });
        logEvent("RecoveryHealthViewed", {
          score: d.health_score || 0, score_band: d.health_category || "",
        });
        if (!localStorage.getItem("recoveros_first_insight")) {
          localStorage.setItem("recoveros_first_insight", "1");
          logEvent("FirstInsightViewed", { health_score: d.health_score || 0 });
        }
      } catch (e) { /* noop */ }
    })
    .catch(() => {})
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openAction = (action, account) => setQa({ open: true, action, account });

  if (loading && !data) return <div className="label-caps mb-6">Loading insights…</div>;
  if (!data) return null;
  if (data.accounts_total === 0 && compact) return null;

  const b = data.health_breakdown || {};
  const rows = compact ? data.top_accounts.slice(0, 5) : data.top_accounts;
  const openAccount = (a, position) => {
    logEvent("priority_account_opened", { account_id: a.account_id, position, view: hero ? "import_hero" : compact ? "dashboard" : "full" });
    navigate(`/accounts/${a.account_id}`);
  };
  const topAcc = (data.top_accounts && data.top_accounts[0]) || null;

  return (
    <div data-testid="insights-panel" className="mb-8">
      {/* Post-import value hero — leads with business value, not "N rows imported" */}
      {hero && (
        <div className="border border-[#16A34A]/30 bg-white p-6 mb-4" data-testid="insights-hero">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 border border-[#16A34A]/30 bg-[#16A34A]/5 text-[#16A34A]">
            <Pulse size={14} weight="bold" />
            <span className="text-[11px] font-bold uppercase tracking-[0.18em]">Your receivables analysis is ready</span>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="border border-border p-4" data-testid="hero-total-outstanding">
              <div className="label-caps">Total Outstanding</div>
              <div className="text-2xl font-black font-heading tracking-tight mt-1 mono text-[#D97706]">{fmtINR(data.total_outstanding)}</div>
            </div>
            <div className="border border-border p-4" data-testid="hero-stuck-90">
              <div className="label-caps">Stuck &gt; 90 Days</div>
              <div className="text-2xl font-black font-heading tracking-tight mt-1 mono text-[#DC2626]">{fmtINR(data.outstanding_over_90)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{fmtNum(data.accounts_over_90)} account{data.accounts_over_90 === 1 ? "" : "s"}</div>
            </div>
            <div className="border border-border p-4" data-testid="hero-needs-attention">
              <div className="label-caps">Accounts Needing Attention</div>
              <div className="text-2xl font-black font-heading tracking-tight mt-1 mono text-[#0B3D91]">{fmtNum(data.accounts_needing_followup)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{fmtINR(data.followup_at_risk)} at risk</div>
            </div>
          </div>
          {topAcc && (
            <div className="mt-3 flex flex-wrap items-center gap-4 border-2 border-[#0B3D91]/25 bg-[#0B3D91]/[0.04] p-4" data-testid="hero-start-here">
              <div className="min-w-[180px]">
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0B3D91]">Start here</div>
                <div className="text-lg font-bold text-slate-900 mt-0.5">{topAcc.borrower_name}</div>
                <div className="text-xs text-muted-foreground mono">{topAcc.loan_id}</div>
              </div>
              <div className="flex items-center gap-5 flex-1">
                <div><div className="label-caps">Outstanding</div><div className="mono font-bold mt-0.5">{fmtINR(topAcc.outstanding_amount)}</div></div>
                <div><div className="label-caps">DPD</div><div className="mono font-bold mt-0.5">{topAcc.dpd}</div></div>
                <div className="min-w-[140px]"><div className="label-caps">Recommended Action</div><span className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-sm ${actionColor(topAcc.recommended_action)}`}>{topAcc.recommended_action}</span></div>
              </div>
              <button onClick={() => openAccount(topAcc, 0)} data-testid="hero-view-account"
                className="inline-flex items-center gap-1.5 bg-[#0B3D91] text-white rounded-sm px-5 py-2.5 text-sm font-bold hover:bg-[#0a347d] transition-colors shrink-0">
                View account <ArrowRight size={15} weight="bold" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Management summary */}
      <div className="relative overflow-hidden border border-[#0B3D91]/30 bg-[#0B3D91] text-white p-6 mb-4" data-testid="management-summary">
        <div className="flex items-start gap-3">
          <Lightning size={22} weight="fill" className="text-[#7ef0a9] shrink-0 mt-0.5" />
          <div>
            <div className="label-caps text-white/70">Management Summary</div>
            <p className="text-base md:text-lg font-medium leading-relaxed mt-1.5 max-w-4xl">{data.management_summary}</p>
          </div>
        </div>
      </div>

      {/* Health + breakdown */}
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <div className="border border-border bg-card p-6 flex items-center gap-5" data-testid="health-card">
          <HealthGauge score={data.health_score} />
          <div>
            <div className="inline-flex items-center gap-1.5 label-caps"><Pulse size={14} weight="bold" /> Recovery Health <InfoTip text="A 0–100 score weighing outstanding, follow-up discipline, recovery rate, settlements and ageing. Higher means a healthier, more recoverable portfolio." /></div>
            <span className={`inline-block mt-2 px-2.5 py-1 text-sm font-bold rounded-sm ${catColor(data.health_category)}`} data-testid="health-category">
              {data.health_category}
            </span>
            <p className="text-xs text-muted-foreground mt-2 max-w-[200px]">Weighted across outstanding, follow-up, recovery, settlement & ageing.</p>
          </div>
        </div>
        <div className="border border-border bg-card p-6 lg:col-span-2" data-testid="health-breakdown">
          <div className="label-caps mb-4">Score Breakdown</div>
          <div className="space-y-3">
            {BREAKDOWN.map((row) => {
              const val = b[row.key] ?? 0;
              return (
                <div key={row.key} data-testid={`breakdown-${row.key}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold">{row.label} <span className="text-muted-foreground font-normal">· {row.weight}</span></span>
                    <span className="mono font-semibold">{val}/100</span>
                  </div>
                  <div className="mt-1 h-2 w-full rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${val}%`, background: scoreColor(val) }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Insight cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <MetricCard icon={CurrencyInr} label="Total Receivables" value={fmtINR(data.total_receivables)} tint="#0B3D91"
          sub={`${fmtNum(data.accounts_total)} accounts`} testId="metric-receivables" />
        <MetricCard icon={CurrencyInr} label="Total Outstanding" value={fmtINR(data.total_outstanding)} tint="#D97706"
          sub={`${data.outstanding_pct}% of receivables`} testId="metric-outstanding" />
        <MetricCard icon={ClockCountdown} label="Accounts Needing Follow-Up" value={fmtNum(data.accounts_needing_followup)} tint="#0B3D91"
          sub={`${fmtINR(data.followup_at_risk)} at risk`} testId="metric-followup" />
        <MetricCard icon={Handshake} label="Settlement Candidates" value={fmtNum(data.settlement_candidates)} tint="#7C3AED"
          sub={`${fmtINR(data.potential_settlement_value)} potential`} testId="metric-settlement" />
      </div>

      {/* Top accounts */}
      <div className="border border-border bg-card" data-testid="top-accounts">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2 font-heading font-bold text-sm">
          <Target size={16} weight="bold" className="text-[#0B3D91]" />
          {compact ? "Top Accounts To Focus On" : "Top 20 Accounts To Focus On"}
          <InfoTip text="Accounts with the most money stuck — ranked by recoverable outstanding so your team chases the highest-impact cases first." />
          {compact && (
            <button onClick={() => navigate("/money-stuck")} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-[#0B3D91] hover:underline" data-testid="insights-view-all">
              View full insights <ArrowRight size={13} weight="bold" />
            </button>
          )}
        </div>
        {rows.length === 0 ? (
          <Empty text="No open accounts to analyze yet. Import accounts to see insights." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="label-caps border-b border-border bg-secondary/40">
                  <th className="text-left p-3 w-10">#</th>
                  <th className="text-left p-3">Customer</th>
                  <th className="text-right p-3">Outstanding</th>
                  <th className="text-center p-3">DPD</th>
                  {!compact && <th className="text-center p-3">Bucket</th>}
                  {!compact && <th className="text-center p-3">Recovery</th>}
                  <th className="text-right p-3">Priority</th>
                  <th className="text-left p-3">Recommended Action</th>
                  <th className="text-center p-3 w-12">Act</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a, i) => (
                  <tr key={a.account_id} onClick={() => openAccount(a, i)}
                    className="border-b border-border last:border-0 hover:bg-secondary/40 cursor-pointer" data-testid={`top-account-row-${i}`}>
                    <td className="p-3 text-muted-foreground mono">{i + 1}</td>
                    <td className="p-3 font-medium whitespace-nowrap">{a.borrower_name}
                      <span className="block text-[11px] text-muted-foreground mono font-normal">{a.loan_id}</span>
                    </td>
                    <td className="p-3 text-right mono font-semibold">{fmtINR(a.outstanding_amount)}</td>
                    <td className="p-3 text-center mono">{a.dpd}</td>
                    {!compact && <td className="p-3 text-center"><BucketBadge bucket={a.bucket} /></td>}
                    {!compact && <td className="p-3 text-center"><span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-sm capitalize ${probColor(a.recovery_probability)}`}>{a.recovery_probability}</span></td>}
                    <td className="p-3 text-right"><span className="mono font-bold" style={{ color: scoreColor(a.priority_score) }}>{a.priority_score}</span></td>
                    <td className="p-3 whitespace-nowrap"><span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-sm ${actionColor(a.recommended_action)}`} data-testid={`top-account-action-${i}`}>{a.recommended_action}</span></td>
                    <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="inline-flex h-7 w-7 items-center justify-center rounded-sm hover:bg-secondary text-muted-foreground" data-testid={`top-account-menu-${i}`} aria-label="Quick actions">
                            <DotsThreeVertical size={18} weight="bold" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => openAction("call", a)} data-testid={`qa-open-call-${i}`}><PhoneCall size={15} weight="bold" className="mr-2 text-[#0B3D91]" /> Log a Call</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openAction("followup", a)} data-testid={`qa-open-followup-${i}`}><CalendarPlus size={15} weight="bold" className="mr-2 text-[#0B3D91]" /> Schedule Follow-Up</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openAction("settlement", a)} data-testid={`qa-open-settlement-${i}`}><Handshake size={15} weight="bold" className="mr-2 text-[#7C3AED]" /> Start Settlement</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <QuickActionDialog open={qa.open} action={qa.action} account={qa.account}
        onOpenChange={(o) => setQa((s) => ({ ...s, open: o }))} onDone={load} />
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, tint, testId }) {
  return (
    <div className="border border-border bg-card p-4 flex items-start gap-3" data-testid={testId}>
      <div className="h-10 w-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${tint}1a` }}>
        <Icon size={20} weight="bold" style={{ color: tint }} />
      </div>
      <div className="min-w-0">
        <div className="label-caps">{label}</div>
        <div className="text-xl md:text-2xl font-black font-heading tracking-tight mt-1 mono truncate">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}
